import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { MultiColumnList, Button, RadioButton, MessageBanner, Loading, } from "@folio/stripes/components";
import { useImportService } from "../../api/importService";
/** The HTTP status of a failed ky request (ky's HTTPError carries the Response). */
var httpStatusOf = function (error) {
    var _a;
    return (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status;
};
export var OwnCloudImportSection = function (_a) {
    var onSuccess = _a.onSuccess, onError = _a.onError;
    var formatMessage = useIntl().formatMessage;
    var queryClient = useQueryClient();
    var _b = useImportService(), fetchOwnCloudFiles = _b.fetchOwnCloudFiles, importFromOwnCloud = _b.importFromOwnCloud;
    var _c = useState(null), selectedFile = _c[0], setSelectedFile = _c[1];
    var _d = useQuery(["ownCloudFiles"], fetchOwnCloudFiles, {
        retry: false,
    }), filesData = _d.data, isLoading = _d.isLoading, isError = _d.isError, error = _d.error;
    var is503Error = isError && error instanceof Error && httpStatusOf(error) === 503;
    var _e = useMutation(function (fileName) { return importFromOwnCloud(fileName); }, {
        onSuccess: function () {
            onSuccess(formatMessage({ id: "ui-br-publishers.import.owncloud.success" }));
            setSelectedFile(null);
            queryClient.invalidateQueries(["ownCloudFiles"]);
        },
        onError: function (err) {
            var status = httpStatusOf(err);
            if (status === 404) {
                onError(formatMessage({ id: "ui-br-publishers.import.owncloud.error.notFound" }));
            }
            else if (status === 503) {
                onError(formatMessage({ id: "ui-br-publishers.import.owncloud.notConfigured" }));
            }
            else {
                onError(formatMessage({ id: "ui-br-publishers.import.owncloud.error.generic" }));
            }
        },
    }), triggerImport = _e.mutate, isImporting = _e.isLoading;
    var handleImport = function () {
        if (selectedFile) {
            triggerImport(selectedFile);
        }
    };
    var handleRefresh = function () {
        queryClient.invalidateQueries(["ownCloudFiles"]);
    };
    var formatFileSize = function (bytes) {
        if (bytes === undefined || bytes === null)
            return "-";
        if (bytes < 1024)
            return "".concat(bytes, " B");
        if (bytes < 1024 * 1024)
            return "".concat((bytes / 1024).toFixed(1), " KB");
        return "".concat((bytes / (1024 * 1024)).toFixed(1), " MB");
    };
    var formatDate = function (dateString) {
        if (!dateString)
            return "-";
        try {
            var date = new Date(dateString);
            return date.toLocaleDateString("cs-CZ", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        }
        catch (_a) {
            return dateString;
        }
    };
    if (isLoading) {
        return React.createElement(Loading, null);
    }
    if (is503Error) {
        return (React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-br-publishers.import.owncloud.notConfigured" })));
    }
    if (isError) {
        return (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-br-publishers.import.owncloud.error" })));
    }
    var files = (filesData === null || filesData === void 0 ? void 0 : filesData.files) || [];
    if (files.length === 0) {
        return (React.createElement(React.Fragment, null,
            React.createElement(MessageBanner, { type: "default" },
                React.createElement(FormattedMessage, { id: "ui-br-publishers.import.owncloud.noFiles" })),
            React.createElement("div", { style: { marginTop: "1rem" } },
                React.createElement(Button, { onClick: handleRefresh },
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.button.refresh" })))));
    }
    var columnMapping = {
        selected: "",
        name: formatMessage({ id: "ui-br-publishers.import.owncloud.column.name" }),
        size: formatMessage({ id: "ui-br-publishers.import.owncloud.column.size" }),
        lastModified: formatMessage({
            id: "ui-br-publishers.import.owncloud.column.lastModified",
        }),
    };
    var formatter = {
        selected: function (item) { return (React.createElement(RadioButton, { checked: selectedFile === item.name, onChange: function () { return setSelectedFile(item.name); }, "aria-label": formatMessage({ id: "ui-br-publishers.import.owncloud.select.aria" }, { fileName: item.name }) })); },
        name: function (item) { return item.name; },
        size: function (item) { return formatFileSize(item.size); },
        lastModified: function (item) { return formatDate(item.lastModified); },
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(MultiColumnList, { contentData: files, columnMapping: columnMapping, formatter: formatter, visibleColumns: ["selected", "name", "size", "lastModified"], columnWidths: {
                selected: "50px",
                name: "40%",
                size: "15%",
                lastModified: "25%",
            }, interactive: false }),
        React.createElement("div", { style: { marginTop: "1rem", display: "flex", gap: "1rem" } },
            React.createElement(Button, { buttonStyle: "primary", onClick: handleImport, disabled: !selectedFile || isImporting }, isImporting ? (React.createElement(FormattedMessage, { id: "ui-br-publishers.import.owncloud.button.importing" })) : (React.createElement(FormattedMessage, { id: "ui-br-publishers.import.owncloud.button.import" }))),
            React.createElement(Button, { onClick: handleRefresh, disabled: isImporting },
                React.createElement(FormattedMessage, { id: "ui-br-publishers.button.refresh" })))));
};
